"use client";

import { StorageCleanup, VersionBasedCleanup } from './localStorage';

// Get version from package.json (this would be injected at build time)
const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || '0.1.0';

export interface DeploymentInitOptions {
  forceCleanup?: boolean;
  preserveUserSession?: boolean;
  preserveSettings?: boolean;
  enableLogging?: boolean;
  onCleanupComplete?: (stats: {
    versionCleanup: number;
    expiredCleanup: number;
    total: number;
    wasForced: boolean;
  }) => void;
}

/**
 * Initialize deployment cleanup on app startup
 */
export function initializeDeploymentCleanup(options: DeploymentInitOptions = {}): void {
  // Only run in browser environment
  if (typeof window === 'undefined') return;

  const {
    forceCleanup = false,
    preserveUserSession = true,
    preserveSettings = true,
    enableLogging = process.env.NODE_ENV === 'development',
    onCleanupComplete
  } = options;

  try {
    // Check if cleanup is needed
    const needsVersionCleanup = VersionBasedCleanup.shouldCleanupForNewVersion(APP_VERSION);
    const needsSchemaCleanup = VersionBasedCleanup.shouldMigrateStorageSchema();

    if (enableLogging && (needsVersionCleanup || needsSchemaCleanup || forceCleanup)) {
      console.log('🚀 [DEPLOYMENT] Starting localStorage cleanup...', {
        appVersion: APP_VERSION,
        needsVersionCleanup,
        needsSchemaCleanup,
        forceCleanup
      });
    }

    // Perform cleanup
    const cleanupStats = StorageCleanup.deploymentCleanup({
      appVersion: APP_VERSION,
      forceCleanup,
      preserveUserSession,
      preserveSettings,
      cleanupExpired: true
    });

    if (enableLogging && cleanupStats.total > 0) {
      console.log('✅ [DEPLOYMENT] localStorage cleanup completed:', cleanupStats);
    }

    // Call completion callback
    if (onCleanupComplete) {
      onCleanupComplete(cleanupStats);
    }

  } catch (error) {
    console.error('❌ [DEPLOYMENT] Error during localStorage cleanup:', error);
  }
}

/**
 * Get deployment status information
 */
export function getDeploymentStatus(): {
  appVersion: string;
  needsCleanup: boolean;
  needsMigration: boolean;
  storageStats: ReturnType<typeof StorageCleanup.getStorageStats>;
} {
  return {
    appVersion: APP_VERSION,
    needsCleanup: VersionBasedCleanup.shouldCleanupForNewVersion(APP_VERSION),
    needsMigration: VersionBasedCleanup.shouldMigrateStorageSchema(),
    storageStats: StorageCleanup.getStorageStats()
  };
}

/**
 * Manual cleanup trigger (for admin/debug purposes)
 */
export function triggerManualCleanup(options: {
  type: 'expired' | 'version' | 'force';
  preserveUserSession?: boolean;
  preserveSettings?: boolean;
}): {
  success: boolean;
  cleanedItems: number;
  error?: string;
} {
  try {
    let cleanedItems = 0;

    switch (options.type) {
      case 'expired':
        const expiredResults = StorageCleanup.cleanupAllExpired();
        cleanedItems = expiredResults.total;
        break;

      case 'version':
        cleanedItems = VersionBasedCleanup.cleanupForNewVersion(APP_VERSION, {
          preserveUserSession: options.preserveUserSession ?? true,
          preserveSettings: options.preserveSettings ?? true,
          logCleanup: true
        });
        break;

      case 'force':
        cleanedItems = VersionBasedCleanup.forceCompleteCleanup(APP_VERSION, true);
        break;
    }

    return {
      success: true,
      cleanedItems
    };

  } catch (error) {
    return {
      success: false,
      cleanedItems: 0,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Development helper to inspect localStorage
 */
export function inspectLocalStorage(): {
  version: ReturnType<typeof VersionBasedCleanup.getStorageInfo>;
  allKeys: string[];
  courtlyKeys: string[];
  expiredKeys: string[];
} {
  if (typeof window === 'undefined') {
    return {
      version: {
        appVersion: null,
        schemaVersion: null,
        totalItems: 0,
        courtlyItems: 0,
        storageSize: 0
      },
      allKeys: [],
      courtlyKeys: [],
      expiredKeys: []
    };
  }

  const version = VersionBasedCleanup.getStorageInfo();
  const allKeys: string[] = [];
  const courtlyKeys: string[] = [];
  const expiredKeys: string[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      allKeys.push(key);

      if (key.startsWith('courtly') || key.startsWith('nextn')) {
        courtlyKeys.push(key);

        // Check if expired
        try {
          const item = localStorage.getItem(key);
          if (item) {
            const parsed = JSON.parse(item);
            if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
              expiredKeys.push(key);
            }
          }
        } catch {
          // Skip non-JSON items
        }
      }
    }
  } catch (error) {
    console.warn('Error inspecting localStorage:', error);
  }

  return {
    version,
    allKeys,
    courtlyKeys,
    expiredKeys
  };
} 